import { defineStore } from 'pinia'

interface ModelTitle {
  rendered: string
}

export interface VehicleModel {
  id: number
  title: ModelTitle
  slug: string
  variant_link: string
  model_image: string
  model_front: string
  vehiclecat: string
  segment: string | string[]
  heading: string
  caption: string
  drive_away_from: string | null
  form: string
  formbg: string
}

interface ModelsState {
  models: VehicleModel[]
  loading: boolean
  error: string | null
  categories: string[]
  isInitialized: boolean
}

export const useModelsStore = defineStore('models', {
  state: (): ModelsState => ({
    models: [],
    loading: false,
    error: null,
    categories: [],
    isInitialized: false,
  }),

  getters: {
    allModels: (state) => state.models,
    
    modelsByCategory: (state) => (category: string) => {
      return state.models.filter(model => {
        return model.vehiclecat === category;
      });
    },
    
    uniqueCategories: (state) => {
      const categories = new Set<string>();
      
      state.models.forEach(model => {
        if (model.vehiclecat) {
          categories.add(model.vehiclecat);
        }
      });
      
      return Array.from(categories).sort();
    }
  },

  actions: {
    _setModelsInternal(models: VehicleModel[]) {
      this.models = models;
      const categories = new Set<string>();
      this.models.forEach(model => {
        if (model.vehiclecat) {
          categories.add(model.vehiclecat);
        }
      });
      this.categories = Array.from(categories).sort();
      this.isInitialized = true;
      this.loading = false;
      this.error = null;
    },

    async fetchModels() {
      return await this.clientFetchModels();
    },

    async initialFetchModels() {
      if (process.client) {
        if (this.isInitialized) return;
        return;
      }

      if (this.isInitialized) {
        return;
      }

      this.loading = true;
      this.error = null;
      try {
        const { data, error, pending } = await useFetch<VehicleModel[]>('/api/models', { 
          key: 'models-ssr-data',
        });

        if (error.value) {
          this.error = error.value.message || 'Failed to load models during SSR';
          this.isInitialized = true; 
        } else if (data.value) {
          this._setModelsInternal(data.value);
        } else {
          this.isInitialized = true;
        }
      } catch (err: any) {
        this.error = err.message || 'A critical server error occurred while fetching models.';
        this.isInitialized = true;
      } finally {
        this.loading = false;
      }
    },
    
    async clientFetchModels() {
      if (this.isInitialized && !this.error) {
        return; // Don't refetch if already initialized without error
      }
      
      this.loading = true;
      this.error = null;
      
      try {
        const timestamp = Date.now();
        const modelsData = await $fetch<VehicleModel[]>(`/api/models?_=${timestamp}`);
        
        if (modelsData) {
          this._setModelsInternal(modelsData);
        } else {
          this.error = 'No models data received from server.';
          this.isInitialized = true;
        }
      } catch (error: any) {
        this.error = error.message || 'Failed to load vehicle models on client';
        this.isInitialized = true;
      } finally {
        this.loading = false;
      }
    },
  }
})